/**
 * ProfileIdentityCardHeader — Shared presentational header used by
 * ProfileJourneyStageSection and ProfileArchetypesSection.
 *
 * Renders:
 *   1. A header row (H3 title)
 *   2. An optional empty-state block (description text + settings link)
 *
 * All BEM class names are derived from the `className` prop supplied by
 * the parent so no dedicated SCSS file is needed — the parent's existing
 * classes (via the community-identity-section mixin) apply directly.
 */

import React from 'react'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import { H3 } from '../../../../Heading/index'

export interface ProfileIdentityCardHeaderProps {
  /** BEM block class name supplied by the parent (e.g. "profile-journey-stage") */
  className: string
  title: string
  isOwner: boolean
  /** When true the section has data — only the header renders. */
  isPopulated: boolean
  /** Empty-state description shown when `!isPopulated && isOwner`. */
  description?: string
  /** Empty-state settings link text. */
  settingsLinkText?: string
  /** Trailing punctuation / text rendered after the settings link. */
  settingsLinkSuffix?: string
  onSettingsClick?: () => void
}

function ProfileIdentityCardHeader ({
  className,
  title,
  isOwner,
  isPopulated,
  description,
  settingsLinkText,
  settingsLinkSuffix,
  onSettingsClick,
}: ProfileIdentityCardHeaderProps) {
  return (
    <>
      <div className={`${className}__header`}>
        <H3 className={`${className}__heading`}>{title}</H3>
      </div>
      {!isPopulated && isOwner && description && (
        <p className={`${className}__description`}>
          {description}
          {settingsLinkText ? (
            <Button
              type={BUTTON_TYPES.LINK}
              className={`${className}__settings-link`}
              onClick={onSettingsClick}
            >
              {settingsLinkText}
            </Button>
          ) : null}
          {settingsLinkSuffix}
        </p>
      )}
    </>
  )
}

export default React.memo(ProfileIdentityCardHeader)
